import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, FileDown, Loader2, Calendar, BadgeCheck } from "lucide-react";
import { PROPERTY, AGENT } from "@/data/property";
import teamPhoto from "@/assets/ramautor-team-photo.webp";
import SectionHeader from "@/components/SectionHeader";
import { downloadBrochure } from "@/lib/brochure";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  preferred_date: "",
  buyer_type: "Private Buyer",
  message: "",
};

const BUYER_TYPES = ["Private Buyer", "Investor", "First-Time Buyer", "Agent-Represented"];

const inputCls =
  "w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-bronze focus:ring-2 focus:ring-bronze/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white";

const labelCls =
  "mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400";

export default function ContactAgent() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please review your details");
      setStatus("idle");
      setForm(EMPTY);
      toast.success(
        data.email_sent
          ? `Enquiry sent — ${AGENT.name.split(" ")[0]} will reply within a few hours.`
          : "Enquiry received — email alerts are temporarily unavailable."
      );
    } catch (err) {
      setStatus("idle");
      toast.error(err.message || `Something went wrong — please call ${AGENT.phone}.`);
    }
  }

  return (
    <section
      id="contact"
      data-testid="agent-contact-section"
      className="bg-[#F4F4F3] py-20 dark:bg-[#101114] sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Agent Contact"
          title="Book your private showing with Shawn"
          sub="Send your enquiry and Shawn replies personally — usually within a few hours. Prefer to talk? Call or WhatsApp below."
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="overflow-hidden rounded-3xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 lg:col-span-2"
          >
            <div className="relative h-80 overflow-hidden">
              <img
                src={teamPhoto}
                alt="The Ramautor Team"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
              <div className="absolute bottom-4 left-6 text-white">
                <p className="font-display text-xl font-bold">{AGENT.name}</p>
                <p className="text-sm text-white/90">{AGENT.title}</p>
              </div>
            </div>
            <div className="p-6">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
                <BadgeCheck size={14} className="text-bronze" /> {AGENT.brokerage}
              </p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <a
                  href={AGENT.phoneHref}
                  data-testid="direct-call-agent-button"
                  className="flex items-center justify-center gap-2 rounded-full bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze dark:bg-white dark:text-black dark:hover:bg-bronze dark:hover:text-white"
                >
                  <Phone size={15} /> {AGENT.phone}
                </a>
                <a
                  href={AGENT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-agent-button"
                  className="flex items-center justify-center gap-2 rounded-full border border-zinc-300 px-4 py-3 text-sm font-semibold text-zinc-800 transition-colors duration-200 hover:border-zinc-900 dark:border-zinc-600 dark:text-zinc-100 dark:hover:border-white"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </div>
              <a
                href={`mailto:${AGENT.email}`}
                data-testid="email-agent-button"
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-zinc-300 px-4 py-3 text-sm font-medium text-zinc-700 transition-colors duration-200 hover:border-zinc-900 dark:border-zinc-600 dark:text-zinc-200 dark:hover:border-white"
              >
                <Mail size={15} /> {AGENT.email}
              </a>
              <button
                data-testid="download-brochure-button"
                onClick={() => {
                  downloadBrochure();
                  toast.success("Brochure downloading — check your files.");
                }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-bronze px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze-dark"
              >
                <FileDown size={15} /> Download Brochure (PDF)
              </button>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-3xl border border-zinc-200 bg-white p-8 dark:border-zinc-800 dark:bg-zinc-900 lg:col-span-3 lg:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className={labelCls}>Full Name *</label>
                <input
                  id="cf-name"
                  data-testid="contact-form-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Appleseed"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-email" className={labelCls}>Email *</label>
                <input
                  id="cf-email"
                  type="email"
                  data-testid="contact-form-email-input"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jane@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-phone" className={labelCls}>Phone</label>
                <input
                  id="cf-phone"
                  data-testid="contact-form-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+1 (___) ___-____"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-date" className={labelCls}>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={12} /> Preferred Tour Date
                  </span>
                </label>
                <input
                  id="cf-date"
                  type="date"
                  data-testid="contact-form-date-input"
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-buyer" className={labelCls}>Buyer Type</label>
                <select
                  id="cf-buyer"
                  data-testid="contact-form-buyer-type-select"
                  value={form.buyer_type}
                  onChange={set("buyer_type")}
                  className={inputCls}
                >
                  {BUYER_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className={labelCls}>Message</label>
                <textarea
                  id="cf-message"
                  data-testid="contact-form-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="We'd love to see the townhouse before the end of the month…"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-form-submit-button"
              disabled={status === "sending"}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-zinc-900 px-6 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-bronze dark:hover:text-white"
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                "Request Private Tour"
              )}
            </button>
            <p className="mt-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
              Your enquiry goes straight to {AGENT.name} and is answered personally.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
